import { useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { ReportsService } from '../api/ReportsAPI';
import { useLightMode } from '../utils';
import { Header } from '../components/Header/Header';
import { DateRangeFilter } from '../components/DateRangeFilter/DateRangeFilter';
import s from '../styles/staff.module.css';

const money = (cents) => `$${((cents || 0) / 100).toLocaleString('en-US', {
  minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Signed, for the adjustments column: a clawback has to read as one.
const signed = (cents) => (cents < 0 ? `−${money(Math.abs(cents))}` : money(cents));

// "Oct 1 – Oct 31" — the range without the year, which the heading above it
// already carries. Falls back to a single date where the range is one day.
const shortRange = (start, end) => {
  if (!start || !end) return '';
  const o = { month: 'short', day: 'numeric', timeZone: 'UTC' };
  const from = new Date(`${start}T12:00:00Z`).toLocaleDateString('en-US', o);
  const to = new Date(`${end}T12:00:00Z`).toLocaleDateString('en-US', o);
  return from === to ? from : `${from} – ${to}`;
};

const initials = (name) => String(name || '').split(' ').filter(Boolean)
  .map(w => w[0]).join('').slice(0, 3).toUpperCase();

function Tile({ label, value, note, tone }) {
  return (
    <div className={s.tipTile}>
      <div className={s.tipTileLabel}>{label}</div>
      <div className={`${s.tipTileValue} ${tone ? s[tone] : ''}`}>{value}</div>
      {note && <div className={s.tipTileNote}>{note}</div>}
    </div>
  );
}

// The rules behind the numbers, written out so nobody has to read the code to
// settle an argument about why a tip landed where it did.
const RULES = [
  {
    title: 'Square decides the tip',
    body: 'A tip exists only once a payment is linked to an appointment. Quoted prices never produce one.',
  },
  {
    title: 'Equal split across assigned crew',
    body: 'Two technicians on one car take half each, rounded in favour of the first assigned.',
  },
  {
    title: 'The crew list freezes at finish',
    body: 'The split is snapshotted when the car is finished. Reassigning crew later cannot rewrite a past tip; it creates an adjustment in the next period.',
  },
  {
    title: 'A tip settles when the car does',
    body: 'Finishing a car with crew named locks that tip to them, and it moves to Already paid out. Until then it is outstanding, because the split can still change. A cash tip is settled the moment it is handed over, finished or not.',
  },
];

export function TipsPage() {
  const [lightMode, toggleLightMode] = useLightMode();
  const [range, setRange] = useState({ start: '', end: '' });
  const onRangeChange = useCallback((r) => setRange(r), []);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['tip-ledger', range.start, range.end],
    queryFn: () => ReportsService.tipLedger(range.start, range.end),
    enabled: !!range.start && !!range.end,
    placeholderData: (prev) => prev,
  });

  const technicians = data?.technicians || [];
  const totals = data?.totals || {};
  const shown = data?.range || {};
  const outstanding = totals.outstandingCents || 0;

  return (
    <div className={lightMode ? `${s.page} ${s.lightPage}` : s.page}>
      <Header lightMode={lightMode} toggleLightMode={toggleLightMode} />

      <div className={s.body} style={{ gridTemplateColumns: '1fr' }}>
        <div>
          <div className={s.tipHead}>
            <div>
              <div className={s.ledgerTileLabel}>Pay period</div>
              <h1 className={s.tipPeriod}>{shown.label || '—'}</h1>
            </div>

            <div className={s.tipHeadRight}>
              <DateRangeFilter onChange={onRangeChange} />
            </div>
          </div>

          <div className={s.tipTiles}>
            <Tile label="Tips earned this period" value={money(totals.earnedCents)}
                  note="locked to crew at finish" />
            <Tile label="Already paid out" value={money(totals.paidCents)}
                  note={shortRange(shown.start, shown.end)
                    ? `${shortRange(shown.start, shown.end)} payout, locked`
                    : 'locked'}
                  tone="ledgerMuted" />
            <Tile label="Adjustments" value={signed(totals.adjustmentsCents || 0)}
                  note={totals.adjustmentCount
                    ? `${totals.adjustmentCount} correction${totals.adjustmentCount === 1 ? '' : 's'} from a locked period`
                    : 'no corrections'}
                  tone={(totals.adjustmentsCents || 0) < 0 ? 'ledgerRed' : undefined} />
            <Tile label="Outstanding tip balance" value={money(outstanding)}
                  note="earned less paid, after adjustments" tone="ledgerGreen" />
            <Tile label="Unattributed" value={money(totals.unattributedCents)}
                  note={totals.unattributedCount
                    ? `${totals.unattributedCount} payment${totals.unattributedCount === 1 ? '' : 's'} with no crew`
                    : 'every tip is credited'}
                  tone={totals.unattributedCents ? 'ledgerRed' : undefined} />
          </div>

          {totals.unattributedCents > 0 && (
            <div className={s.tipWarn}>
              <span>
                {totals.unattributedCount === 1 ? 'One payment carries' : `${totals.unattributedCount} payments carry`}
                {' '}{money(totals.unattributedCents)} in tips with no appointment attached,
                so no technician can be paid from {totals.unattributedCount === 1 ? 'it' : 'them'}.
              </span>
              <Link
                to={`/attention?start=${range.start}&end=${range.end}`}
                className={s.tipWarnLink}
              >
                Resolve in Needs attention
              </Link>
            </div>
          )}

          {isLoading && <div className={s.ledgerIntro}>Loading…</div>}
          {isError && <div className={s.remitError}>Couldn’t load the tip ledger.</div>}

          {!isLoading && !isError && !technicians.length && (
            <div className={s.ledgerIntro}>No tips credited in this period.</div>
          )}

          {technicians.length > 0 && (
            <div className={s.tableWrap}>
              <table className={s.table}>
                <thead>
                  <tr>
                    <th>Technician</th>
                    <th className={s.num}>Jobs</th>
                    <th className={s.num}>Earned</th>
                    <th className={s.num}>Adjustments</th>
                    <th className={s.num}>Settled</th>
                    <th className={s.num}>Outstanding</th>
                  </tr>
                </thead>
                <tbody>
                  {technicians.map(t => (
                    <tr key={t.externalId}>
                      <td>
                        <span className={s.tipAvatar}>{initials(t.name)}</span>
                        <strong className={s.tipName}>{t.name}</strong>
                      </td>
                      <td className={s.num}>{t.jobs}</td>
                      <td className={s.num}><strong>{money(t.earnedCents)}</strong></td>
                      <td className={`${s.num} ${t.adjustmentsCents < 0 ? s.ledgerRed : s.ledgerMuted}`}>
                        {signed(t.adjustmentsCents)}
                      </td>
                      <td className={`${s.num} ${s.ledgerMuted}`}>{money(t.paidCents)}</td>
                      <td className={s.num}><strong>{money(t.outstandingCents)}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className={s.tipRules}>
            {RULES.map(r => (
              <div key={r.title} className={s.tipRule}>
                <div className={s.tipRuleTitle}>{r.title}</div>
                <div className={s.tipRuleBody}>{r.body}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
